import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { COUNTRIES } from "../utils/countries";
import "./AccountTypeModal.css";

const ACCOUNT_TYPES = [
  {
    value: "user",
    label: "User",
    description: "Browse and join discussions on the forum.",
  },
  {
    value: "intern",
    label: "Intern",
    description: "Looking for internships and learning opportunities.",
  },
  {
    value: "employer",
    label: "Employer",
    description: "Post jobs and hire candidates.",
  },
];

const AccountTypeModal = ({
  open,
  email,
  onConfirm,
  onCancel,
  loading = false,
}) => {
  const [step, setStep] = useState("type");
  const [typeofuser, setTypeofuser] = useState("");
  const [country, setCountry] = useState("");
  const [position, setPosition] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setStep("type");
    setTypeofuser("");
    setCountry("");
    setPosition("");
    setPassword("");
    setConfirmPassword("");
    setError("");
  }, [open]);

  if (!open) return null;

  const selectedType = ACCOUNT_TYPES.find((type) => type.value === typeofuser);

  const goToDetails = () => {
    if (!typeofuser) {
      setError("Please select whether you are a User, Intern, or Employer.");
      return;
    }
    setError("");
    setStep("details");
  };

  const goToPassword = () => {
    if (!country) {
      setError("Please select your country.");
      return;
    }
    if (!position.trim()) {
      setError("Please enter your position or job title.");
      return;
    }
    setError("");
    setStep("password");
  };

  const handleConfirm = () => {
    if (!password || password.length < 8) {
      setError("Please set a password with at least 8 characters.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    setError("");
    onConfirm({
      typeofuser,
      password,
      country,
      position: position.trim(),
    });
  };

  return createPortal(
    <div
      className="atm-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="account-type-title"
    >
      <div className="atm-card">
        {step === "type" ? (
          <>
            <h2 id="account-type-title" className="atm-title">
              Choose your account type
            </h2>
            <p className="atm-subtitle">
              {email
                ? `Welcome! Tell us how you will use COOL (${email}).`
                : "Welcome! Tell us how you will use COOL."}
            </p>

            <div className="atm-options">
              {ACCOUNT_TYPES.map((type) => (
                <button
                  key={type.value}
                  type="button"
                  className={`atm-option${typeofuser === type.value ? " selected" : ""}`}
                  onClick={() => {
                    setTypeofuser(type.value);
                    setError("");
                  }}
                  disabled={loading}
                >
                  <strong>{type.label}</strong>
                  <span>{type.description}</span>
                </button>
              ))}
            </div>

            {error && <div className="atm-error">{error}</div>}

            <div className="atm-actions">
              <button
                type="button"
                className="atm-btn atm-btn-cancel"
                onClick={onCancel}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="atm-btn atm-btn-primary"
                onClick={goToDetails}
                disabled={loading}
              >
                Continue
              </button>
            </div>
          </>
        ) : step === "details" ? (
          <>
            <h2 id="account-type-title" className="atm-title">
              Country and position
            </h2>
            <p className="atm-subtitle">
              Tell us where you are and the role you currently have.
            </p>
            {selectedType && (
              <div className="atm-selected-type">
                Account type: <strong>{selectedType.label}</strong>
              </div>
            )}

            <div className="atm-password">
              <div className="atm-field">
                <label className="atm-label" htmlFor="atm-country">
                  Country
                </label>
                <select
                  id="atm-country"
                  className="atm-input"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  disabled={loading}
                >
                  <option value="">Select country</option>
                  {COUNTRIES.map((c) => (
                    <option key={c.code} value={c.name}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="atm-field">
                <label className="atm-label" htmlFor="atm-position">
                  Position
                </label>
                <input
                  id="atm-position"
                  className="atm-input"
                  type="text"
                  value={position}
                  onChange={(e) => setPosition(e.target.value)}
                  placeholder="e.g. Software Engineer"
                  maxLength={191}
                  disabled={loading}
                />
              </div>
            </div>

            {error && <div className="atm-error">{error}</div>}

            <div className="atm-actions">
              <button
                type="button"
                className="atm-btn atm-btn-cancel"
                onClick={() => {
                  setError("");
                  setStep("type");
                }}
                disabled={loading}
              >
                Back
              </button>
              <button
                type="button"
                className="atm-btn atm-btn-primary"
                onClick={goToPassword}
                disabled={loading}
              >
                Continue
              </button>
            </div>
          </>
        ) : (
          <>
            <h2 id="account-type-title" className="atm-title">
              Set a password
            </h2>
            <p className="atm-subtitle">
              Create a password so you can also sign in with your email later.
            </p>
            {selectedType && (
              <div className="atm-selected-type">
                {selectedType.label}
                {country ? ` · ${country}` : ""}
                {position.trim() ? ` · ${position.trim()}` : ""}
              </div>
            )}

            <div className="atm-password">
              <div className="atm-field">
                <label className="atm-label" htmlFor="atm-password">
                  Password
                </label>
                <input
                  id="atm-password"
                  className="atm-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  autoComplete="new-password"
                  disabled={loading}
                />
              </div>
              <div className="atm-field">
                <label className="atm-label" htmlFor="atm-confirm-password">
                  Confirm password
                </label>
                <input
                  id="atm-confirm-password"
                  className="atm-input"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter password"
                  autoComplete="new-password"
                  disabled={loading}
                />
              </div>
            </div>

            {error && <div className="atm-error">{error}</div>}

            <div className="atm-actions">
              <button
                type="button"
                className="atm-btn atm-btn-cancel"
                onClick={() => {
                  setError("");
                  setStep("details");
                }}
                disabled={loading}
              >
                Back
              </button>
              <button
                type="button"
                className="atm-btn atm-btn-primary"
                onClick={handleConfirm}
                disabled={loading}
              >
                {loading ? "Creating..." : "Create account"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
};

export default AccountTypeModal;
